/** Same heading on the filter pane and in + Add slicer. */
export const SCOPE_SLICER_LABEL = 'Baseline / Extra (EX)';

/** Built-in histogram / P6 slicers (already on the pane). */
export const BUILTIN_SLICERS = [
  { id: 'p6schedules', label: 'Budget / Current XER (consolidate several; scenario chips pick a calendar per package)', group: 'Histogram' },
  { id: 'series', label: 'Curve (Budget / Earned / Remaining / Spent)', group: 'Histogram' },
  { id: 'units', label: 'Man-hours / Man-weeks', group: 'Histogram' },
  { id: 'groupby', label: 'Group By', group: 'Histogram' },
  { id: 'weekstart', label: 'Week starts Mon / Sun', group: 'Histogram' },
  { id: 'facformula', label: 'FAC / ETC formula', group: 'Histogram' },
  { id: 'assumelastweek', label: 'Assume this week = last week', group: 'Histogram' },
  { id: 'safety', label: 'Safety staffing (1 per N, first-then, or three-step)', group: 'Histogram' },
  { id: 'supervision', label: 'Supervision (FM / GF)', group: 'Histogram' },
  { id: 'staffnums', label: 'Counts on bars', group: 'Histogram' },
  { id: 'staffplan', label: 'Staffing plan (baseline / smooth / view)', group: 'Histogram' },
  { id: 'shopfield', label: 'Fab / Field', group: 'Histogram' },
  { id: 'activities', label: 'Activities (P6 WBS tree)', group: 'P6 / Schedule' },
  { id: 'discipline', label: 'Discipline', group: 'P6 / Schedule' },
  { id: 'resources', label: 'Resource ID', group: 'P6 / Schedule' },
  { id: 'rsrcname', label: 'Resource Name', group: 'P6 / Schedule' },
  { id: 'legendby', label: 'Legend by (one colour / discipline / resource / P6 column) and place', group: 'Histogram' },
  { id: 'chartspan', label: 'Presentation (fit the screen, or scroll)', group: 'Histogram' },
  { id: 'family', label: 'KPI family', group: 'P6 / Schedule' },
  { id: 'scope', label: SCOPE_SLICER_LABEL, group: 'P6 / Schedule' },
  { id: 'fab', label: 'Fab view', group: 'P6 / Schedule' },
  { id: 'phase', label: 'Budget source', group: 'P6 / Schedule' },
  { id: 'earnedsrc', label: 'Earned source (as-is / temp / perm / manual)', group: 'P6 / Schedule' },
  { id: 'calendar', label: 'P6 Calendar', group: 'P6 / Schedule' },
  { id: 'whatif', label: 'What-if calendars', group: 'P6 / Schedule' },
  { id: 'spentfeed', label: 'Spent feed', group: 'P6 / Schedule' }
];

/** Shown until the API returns the live column catalog (every TT / employee column). */
export const FALLBACK_ACTUALS_SLICERS = [
  { id: 'empl_name', label: 'Empl Name', group: 'Time Tracker / Actuals', defaultOn: false },
  { id: 'trade', label: 'Trade', group: 'Time Tracker / Actuals', defaultOn: false },
  { id: 'division', label: 'Division', group: 'Time Tracker / Actuals', defaultOn: false },
  { id: 'op_code', label: 'Op Code', group: 'Time Tracker / Actuals', defaultOn: false }
];

/** @deprecated use FALLBACK_ACTUALS_SLICERS — kept so older imports do not break */
export const ACTUALS_SLICERS = FALLBACK_ACTUALS_SLICERS;

const PANEL_KEY_ALIASES = {
  employees: 'empl_name',
  trades: 'trade',
  divisions: 'division',
  op_codes: 'op_code',
  op_desc: 'op_code_description'
};

export const DEFAULT_ACTUALS_ON = FALLBACK_ACTUALS_SLICERS.filter((s) => s.defaultOn).map((s) => s.id);

/** Compact toolbar defaults — everything else lives in Settings or + Add slicer. */
export const DEFAULT_TOOLBAR_ON = [
  'p6schedules', 'series', 'units', 'groupby', 'legendby', 'chartspan', 'shopfield',
  'discipline', 'resources', 'rsrcname', 'activities'
];

export const SETTINGS_SLICER_IDS = [
  'earnedsrc', 'facformula', 'assumelastweek', 'calendar', 'spentfeed', 'phase',
  'fab', 'safety', 'supervision', 'staffplan', 'staffnums', 'whatif', 'weekstart'
];

/** Drawer defaults (checked on first visit). The user can uncheck any of them. */
export const PANE_PINNED_IDS = DEFAULT_TOOLBAR_ON;

export function isSettingsSlicer(id) {
  return SETTINGS_SLICER_IDS.includes(id);
}

/** Stable A–Z order for the right filter drawer. */
export function sortFiltersByLabel(rows) {
  return [...(rows || [])].sort((a, b) =>
    String(a.label || '').localeCompare(String(b.label || ''), undefined, { sensitivity: 'base' })
  );
}

export const SLICER_KINDS = [
  { id: 'list', label: 'Checkbox list' },
  { id: 'dropdown', label: 'Dropdown' }
];

export const EMPTY_ACTUALS_FILTERS = {};

export const DEFAULT_VISIBLE_PANELS = Object.fromEntries([
  ...BUILTIN_SLICERS.map((s) => [s.id, DEFAULT_TOOLBAR_ON.includes(s.id)]),
  ...FALLBACK_ACTUALS_SLICERS.map((s) => [s.id, !!s.defaultOn])
]);

export const EVM_SLICER_PANELS_KEY = 'evm-slicer-panels-v3';
export const FILTER_LAYOUT_VERSION = 3;

export function loadVisiblePanels() {
  try {
    const fromV3 = !!localStorage.getItem(EVM_SLICER_PANELS_KEY);
    const raw = localStorage.getItem(EVM_SLICER_PANELS_KEY)
      || localStorage.getItem('evm-slicer-panels-v2');
    if (!raw) return { ...DEFAULT_VISIBLE_PANELS };
    const parsed = JSON.parse(raw);
    const next = { ...DEFAULT_VISIBLE_PANELS };
    for (const [k, v] of Object.entries(parsed || {})) {
      next[PANEL_KEY_ALIASES[k] || k] = v;
    }
    SETTINGS_SLICER_IDS.forEach((id) => { next[id] = false; });
    // First v3 read from v2: restore the drawer defaults (Legend used to
    // vanish when v2 hid `legendby`). After that, honor check / uncheck.
    if (!fromV3) {
      DEFAULT_TOOLBAR_ON.forEach((id) => { next[id] = true; });
      next.family = false;
      next.scope = false;
    }
    return next;
  } catch {
    return { ...DEFAULT_VISIBLE_PANELS };
  }
}

export function saveVisiblePanels(panels) {
  try {
    localStorage.setItem(EVM_SLICER_PANELS_KEY, JSON.stringify(panels));
  } catch {
    /* ignore */
  }
}

export function isSlicerVisible(panels, id, defaultOn = false) {
  if (panels?.[id] === true) return true;
  if (panels?.[id] === false) return false;
  return !!defaultOn;
}

export function toActualsSlicer(field) {
  return {
    id: field.id,
    label: field.label,
    group: field.group || 'Time Tracker / Actuals',
    column: field.column,
    table: field.table,
    defaultOn: DEFAULT_ACTUALS_ON.includes(field.id)
  };
}

export const FALLBACK_SLICER_LAYOUT = {
  order: 80,
  w: 160,
  h: 148,
  search: true,
  scroll: true,
  kind: 'list'
};
